/**
 * Category nav menu — two visual modes:
 * <ul>
 *   <li>{@code 'horizontal'}: scrolling pill row (scroll-snap, no JS)</li>
 *   <li>{@code 'vertical'}: stacked list, optional thumbnails</li>
 * </ul>
 * Ported from ThemeMaker's {@code blocks/nav/CategoryMenu}.
 *
 * <p>Author supplies pre-resolved {@code items} — no orchestrator
 * coupling. The horizontal variant uses CSS-only scroll-snap rather
 * than the legacy arrow-button overflow client (kept the block static).
 */
import { tx, asList, pickEnum, asBool } from '../_shared/coerce';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface CategoryMenuItem {
  id?: string | number;
  name?: Localized;
  slug?: string;
  count?: number;
  image?: string;
  color?: string;
  href?: string;
}

export interface CategoryMenuProps {
  title?: Localized;
  placement?: 'horizontal' | 'vertical';
  items?: CategoryMenuItem[];
  activeSlug?: string;
  showCounts?: boolean;
  showImages?: boolean;
  itemHref?: (item: CategoryMenuItem) => string;
}

export function CategoryMenu({
  title,
  placement,
  items,
  activeSlug,
  showCounts,
  showImages,
  itemHref,
}: CategoryMenuProps) {
  const p = pickEnum<'horizontal' | 'vertical'>(
    placement,
    ['horizontal', 'vertical'] as const,
    'horizontal',
  );
  const list = asList<CategoryMenuItem>(items);
  const t = tx(title);
  const sc = asBool(showCounts, false);
  const si = asBool(showImages, false);

  if (list.length === 0) return null;

  const hrefFor = (c: CategoryMenuItem): string => {
    if (itemHref) return itemHref(c);
    if (c.href) return c.href;
    return c.slug ? `/c/${c.slug}` : '#';
  };

  if (p === 'horizontal') {
    return (
      <BlockRoot as="nav" aria-label={t || 'Categories'} style={{ width: '100%' }}>
        {t && (
          <div
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 'var(--font-tracking-wider, 0.06em)',
              color: 'var(--color-text-secondary, #71717a)',
              marginBottom: 8,
            }}
          >
            {t}
          </div>
        )}
        <style
          dangerouslySetInnerHTML={{
            __html: `
          .lms-cat-pills { display: flex; gap: 8px; overflow-x: auto; scroll-snap-type: x mandatory; padding: 2px 0 8px; -webkit-overflow-scrolling: touch; scrollbar-width: thin; }
          .lms-cat-pills::-webkit-scrollbar { height: 4px; }
          .lms-cat-pills::-webkit-scrollbar-thumb { background: var(--color-neutral-200, #e5e7eb); border-radius: 999px; }
          .lms-cat-pill { scroll-snap-align: start; flex-shrink: 0; padding: 6px 14px; border-radius: 999px; border: 1px solid var(--color-border-default, #e5e7eb); background: var(--color-surface-card, #fff); color: var(--color-text-secondary, #52525b); font-size: 13px; font-weight: 500; text-decoration: none; white-space: nowrap; transition: background 120ms ease, color 120ms ease, border-color 120ms ease; }
          .lms-cat-pill:hover { background: var(--color-surface-page, #f4f4f5); }
          .lms-cat-pill[aria-current="true"] { background: var(--color-text-primary, #18181b); color: var(--color-surface-card, #fff); border-color: var(--color-text-primary, #18181b); }
        `,
          }}
        />
        <div className="lms-cat-pills">
          {list.map((c, i) => {
            const isActive = activeSlug != null && c.slug === activeSlug;
            const name = tx(c.name);
            const countStr =
              sc && typeof c.count === 'number' && c.count > 0 ? ` (${c.count})` : '';
            return (
              <a
                key={c.id ?? i}
                href={hrefFor(c)}
                className="lms-cat-pill"
                aria-current={isActive ? 'true' : undefined}
              >
                {name}
                {countStr}
              </a>
            );
          })}
        </div>
      </BlockRoot>
    );
  }

  return (
    <BlockRoot
      as="nav"
      aria-label={t || 'Categories'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
      }}
    >
      {t && (
        <div
          style={{
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 'var(--font-tracking-wider, 0.06em)',
            color: 'var(--color-text-secondary, #71717a)',
            padding: 'var(--space-2, 8px) var(--space-3, 12px) var(--space-1, 4px)',
          }}
        >
          {t}
        </div>
      )}
      {list.map((c, i) => {
        const isActive = activeSlug != null && c.slug === activeSlug;
        return (
          <a
            key={c.id ?? i}
            href={hrefFor(c)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 'var(--space-2, 8px) var(--space-3, 12px)',
              borderRadius: 'var(--radius-sm, 6px)',
              textDecoration: 'none',
              color: isActive
                ? 'var(--color-text-primary, #18181b)'
                : 'var(--color-text-secondary, #52525b)',
              fontWeight: isActive ? 600 : 500,
              fontSize: 'var(--font-size-sm, 14px)',
              background: isActive ? 'var(--color-surface-page, #f4f4f5)' : 'transparent',
              transition: 'background 120ms ease',
            }}
          >
            {si && (
              <span
                aria-hidden
                style={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  overflow: 'hidden',
                  background: c.image
                    ? `url("${c.image}") center/cover`
                    : c.color || 'var(--color-neutral-200, #e5e7eb)',
                }}
              />
            )}
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {tx(c.name)}
            </span>
            {sc && typeof c.count === 'number' && c.count > 0 && (
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--color-text-secondary, #71717a)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {c.count}
              </span>
            )}
          </a>
        );
      })}
    </BlockRoot>
  );
}
